import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase/firebase';
import { collection, query, getDocs, doc, updateDoc, onSnapshot } from "firebase/firestore";

// ===================================================================
// Metric Card Component
// ===================================================================
const MetricCard = ({ title, value, icon, color }) => {
    const valueDisplay = value === null || value === 'ERR' ? "N/A" : value.toLocaleString();
    const colorClasses = {
        blue: "bg-blue-50 text-blue-600 border-blue-500",
        green: "bg-green-50 text-green-600 border-green-500",
        red: "bg-red-50 text-red-600 border-red-500"
    };
    const cls = colorClasses[color] || "bg-gray-50 text-gray-600 border-gray-500";

    return (
        <div className={`p-5 rounded-lg shadow-sm border-l-4 ${cls} flex items-center space-x-4 bg-white`}>
            <div className={`text-3xl p-3 rounded-full ${cls}`}>{icon}</div>
            <div>
                <p className="text-sm font-medium text-gray-500">{title}</p>
                <p className="text-2xl font-bold text-gray-800">{valueDisplay}</p>
            </div>
        </div>
    );
};

// ===================================================================
// Manual Staff Form
// ===================================================================
const ManualStaffForm = ({ setOwnerActionMessage, setIsOwnerActionError }) => {
    const { appointStaff } = useAuth();
    const [role, setRole] = useState('educator');
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setFormError('');
        setFormSuccess('');

        try {
            const result = await appointStaff({ email, role });
            setFormSuccess(result.message || `Successfully appointed ${email} as ${role}.`);
            setOwnerActionMessage(result.message || `Staff appointed: ${email}`);
            setIsOwnerActionError(false);
            setEmail('');
        } catch (err) {
            setFormError(err.message || "Failed to appoint staff.");
            setOwnerActionMessage("Error appointing staff.");
            setIsOwnerActionError(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-gray-600">Appoint an existing user to a new staff role.</p>
            {formError && <div className="text-red-600 bg-red-100 p-3 rounded">{formError}</div>}
            {formSuccess && <div className="text-green-600 bg-green-100 p-3 rounded">{formSuccess}</div>}
            <div>
                <label className="block text-sm font-medium">Role</label>
                <select value={role} onChange={(e) => setRole(e.target.value)} className="mt-1 block w-full p-2 border rounded-md shadow-sm">
                    <option value="owner">Owner (Highest Privilege)</option>
                    <option value="exam_admin">Admin (Exams)</option>
                    <option value="learning_admin">Admin (Learning)</option>
                    <option value="courses_admin">Admin (Courses)</option>
                    <option value="events_admin">Admin (Events)</option>
                    <option value="finance_admin">Admin (Finance)</option>
                    <option value="grievances_admin">Admin (Grievances)</option>
                    <option value="educator">Educator</option>
                    <option value="examiner">Examiner</option>
                </select>
            </div>
            <div>
                <label className="block text-sm font-medium">User's Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user.email@example.com" required className="mt-1 block w-full p-2 border rounded-md shadow-sm" />
            </div>
            <button type="submit" disabled={loading} className="w-full bg-red-600 text-white py-2 px-4 rounded-md font-semibold hover:bg-red-700 disabled:opacity-50">
                {loading ? 'Appointing...' : 'Appoint Staff Role'}
            </button>
        </form>
    );
};

// ===================================================================
// User Management Table
// ===================================================================
const UserManagementTable = ({ ownerId, setOwnerActionMessage, setIsOwnerActionError }) => {
    const { updateUserStatus, deleteUserAccount } = useAuth();
    const [users, setUsers] = useState([]);
    const [listLoading, setListLoading] = useState(true);
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [isMounted, setIsMounted] = useState(true);

    useEffect(() => () => { setIsMounted(false); }, []);

    const fetchUsers = useCallback(async () => {
        if (!isMounted) return;
        setListLoading(true);
        try {
            const q = query(collection(db, "users"));
            const snapshot = await getDocs(q);
            const userList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            if (isMounted) setUsers(userList);
        } catch (error) {
            console.error(error);
            if (isMounted) {
                setOwnerActionMessage("Error loading user list.");
                setIsOwnerActionError(true);
            }
        } finally {
            if (isMounted) setListLoading(false);
        }
    }, [isMounted, setOwnerActionMessage, setIsOwnerActionError]);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    const handleAction = async (userId, action, isEnabled) => {
        if (userId === ownerId) {
            setOwnerActionMessage(`Cannot ${action} the current primary Owner.`);
            setIsOwnerActionError(true);
            return;
        }
        if (action === 'delete' && !window.confirm(`Delete user ${userId}? This cannot be undone.`)) return;

        setActionLoadingId(userId);
        setOwnerActionMessage('');
        setIsOwnerActionError(false);

        try {
            if (action === 'delete') {
                await deleteUserAccount(userId);
                setOwnerActionMessage(`User ${userId} deleted successfully.`);
                setUsers(prev => prev.filter(u => u.id !== userId));
            } else {
                await updateUserStatus(userId, isEnabled);
                setOwnerActionMessage(`User status updated to ${isEnabled ? 'ENABLED' : 'DISABLED'}.`);
                setUsers(prev => prev.map(u => u.id === userId ? { ...u, isDisabled: !isEnabled } : u));
            }
        } catch (error) {
            console.error(error);
            setOwnerActionMessage(`Failed to ${action} user: ${error.message}`);
            setIsOwnerActionError(true);
        } finally {
            setActionLoadingId(null);
        }
    };

    if (listLoading) return <p className="text-center py-8">Loading user data...</p>;

    return (
        <div className="overflow-x-auto">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Registered Users ({users.length})</h3>
            <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-4 py-3 text-left font-medium text-gray-500">User / Email</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-500">Role</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-500">Actions</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {users.map(user => (
                        <tr key={user.id}>
                            <td className="px-4 py-3">
                                <div className="font-medium text-gray-900">{user.displayName || 'N/A'}</div>
                                <div className="text-xs text-gray-500">{user.email}</div>
                            </td>
                            <td className="px-4 py-3 text-gray-700">{user.role || 'student'}</td>
                            <td className="px-4 py-3">
                                <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${user.isDisabled ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                                    {user.isDisabled ? 'Disabled' : 'Active'}
                                </span>
                            </td>
                            <td className="px-4 py-3 flex space-x-2">
                                <button onClick={() => handleAction(user.id, user.isDisabled ? 'enable' : 'disable', !user.isDisabled)}
                                        disabled={actionLoadingId === user.id}
                                        className={`text-xs font-semibold px-2 py-1 rounded transition ${user.isDisabled ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-yellow-500 hover:bg-yellow-600 text-black'}`}>
                                    {actionLoadingId === user.id ? 'Wait...' : user.isDisabled ? 'Enable' : 'Disable'}
                                </button>
                                <button onClick={() => handleAction(user.id, 'delete')}
                                        disabled={actionLoadingId === user.id}
                                        className="text-xs font-semibold px-2 py-1 rounded bg-red-500 hover:bg-red-600 text-white transition">
                                    {actionLoadingId === user.id ? 'Wait...' : 'Delete'}
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// ===================================================================
// Main Admin / Owner Component
// ===================================================================
export default function AdminOwnerContent() {
    const { user, userProfile, isAcceptingStaff, toggleStaffApplications, approveStaffApplication } = useAuth();
    const navigate = useNavigate();

    const [stats, setStats] = useState({ totalUsers: null, pendingApps: null });
    const [applications, setApplications] = useState([]);
    const [selectedApp, setSelectedApp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('inbox');

    const role = userProfile?.role?.toLowerCase();
    const isOwnerRole = role === 'owner';
    const canManageStaff = isOwnerRole || role?.includes('admin') || role === 'educator' || role === 'examiner';
    const canAccessManagement = isOwnerRole || role?.includes('admin');

    const [ownerActionMessage, setOwnerActionMessage] = useState('');
    const [isOwnerActionError, setIsOwnerActionError] = useState(false);
    const [isApproving, setIsApproving] = useState(false);

    const [isMounted, setIsMounted] = useState(true);
    useEffect(() => () => { setIsMounted(false); }, []);

    // Fetch Stats + Applications
    useEffect(() => {
        const fetchPlatformStats = async () => {
            try {
                const usersSnap = await getDocs(collection(db, "users"));
                if (isMounted) setStats(prev => ({ ...prev, totalUsers: usersSnap.size }));
            } catch (error) {
                console.error(error);
                if (isMounted) setStats(prev => ({ ...prev, totalUsers: 'ERR' }));
            }
        };
        fetchPlatformStats();

        if (!userProfile || !canManageStaff) { setLoading(false); return; }

        const q = query(collection(db, "public_applications"));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            if (!isMounted) return;
            const appList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setApplications(appList);
            setStats(prev => ({ ...prev, pendingApps: appList.filter(a => a.status === 'pending_review').length }));
            if (loading) setLoading(false);
        }, (error) => {
            console.error(error);
            setOwnerActionMessage(`Error loading applications: ${error.message}`);
            setIsOwnerActionError(true);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [userProfile, canManageStaff, isMounted, loading]);

    const updateStatus = async (appId, newStatus) => {
        setOwnerActionMessage('');
        setIsOwnerActionError(false);
        setIsApproving(true);

        try {
            await updateDoc(doc(db, "public_applications", appId), {
                status: newStatus,
                reviewedBy: userProfile?.uid,
                reviewedAt: new Date()
            });
            setOwnerActionMessage(`Application successfully ${newStatus}.`);
            setSelectedApp(null);
        } catch (error) {
            console.error(error);
            setOwnerActionMessage(`Failed to update status: ${error.message}`);
            setIsOwnerActionError(true);
        } finally {
            setIsApproving(false);
        }
    };

    const handleApprove = async (appId) => {
        if (!isOwnerRole) {
            setOwnerActionMessage("Only Owner can approve.");
            setIsOwnerActionError(true);
            return;
        }

        setIsApproving(true);
        try {
            const result = await approveStaffApplication(appId);
            setOwnerActionMessage(result.message || "Application approved and account created!");
            setSelectedApp(null);
        } catch (error) {
            console.error(error);
            setOwnerActionMessage(`Approval failed: ${error.message}`);
            setIsOwnerActionError(true);
        } finally { setIsApproving(false); }
    };

    const handleToggle = async () => {
        if (!isOwnerRole) { setOwnerActionMessage("Only Owner can toggle portal."); setIsOwnerActionError(true); return; }

        const shouldOpen = !isAcceptingStaff;
        try {
            await toggleStaffApplications(shouldOpen);
            setOwnerActionMessage(`Portal applications now ${shouldOpen ? 'OPEN' : 'CLOSED'}.`);
            if (shouldOpen) navigate('/apply/staff');
        } catch (error) {
            console.error(error);
            setOwnerActionMessage(`Failed to toggle portal: ${error.message}`);
            setIsOwnerActionError(true);
        }
    };

    if (loading) return <p>Loading Platform Data...</p>;

    return (
        <div className="space-y-8">
            {ownerActionMessage && (
                <div className={`p-4 rounded-md shadow-sm ${isOwnerActionError ? 'bg-red-100 border-l-4 border-red-500 text-red-700' : 'bg-green-100 border-l-4 border-green-500 text-green-700'}`}>
                    <p className="font-medium">{ownerActionMessage}</p>
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <MetricCard title="Total Users" value={stats.totalUsers} icon="👥" color="blue" />
                <MetricCard title="Pending Applications" value={stats.pendingApps} icon="📄" color="green" />
                <MetricCard title="Portal Status" value={isAcceptingStaff ? 'Open' : 'Closed'} icon={isAcceptingStaff ? '✅' : '❌'} color={isAcceptingStaff ? 'green' : 'red'} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-sm border">
                    {/* Tabs */}
                    <div className="border-b border-gray-200 mb-6">
                        <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                            <button onClick={() => setActiveTab('inbox')} className={`whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'inbox' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Application Inbox</button>
                            {canManageStaff && <button onClick={() => setActiveTab('manual')} className={`whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'manual' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Appoint Staff Roles</button>}
                            {canAccessManagement && <button onClick={() => setActiveTab('management')} className={`whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'management' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>User Management</button>}
                        </nav>
                    </div>

                    {/* Tab Content */}
                    {activeTab === 'inbox' && (
                        <div>
                            {applications.length === 0 ? <p>No pending applications.</p> :
                                applications.map(app => (
                                    <div key={app.id} className="p-4 mb-3 border rounded shadow-sm">
                                        <p><strong>{app.name}</strong> ({app.email})</p>
                                        <p>Role requested: {app.requestedRole}</p>
                                        <p>Status: {app.status}</p>
                                        <div className="flex space-x-2 mt-2">
                                            <button disabled={isApproving} onClick={() => updateStatus(app.id, 'approved')} className="px-2 py-1 rounded bg-green-500 text-white hover:bg-green-600">Approve</button>
                                            <button disabled={isApproving} onClick={() => updateStatus(app.id, 'rejected')} className="px-2 py-1 rounded bg-red-500 text-white hover:bg-red-600">Reject</button>
                                        </div>
                                    </div>
                                ))
                            }
                        </div>
                    )}

                    {activeTab === 'manual' && <ManualStaffForm setOwnerActionMessage={setOwnerActionMessage} setIsOwnerActionError={setIsOwnerActionError} />}

                    {activeTab === 'management' && <UserManagementTable ownerId={user?.uid} setOwnerActionMessage={setOwnerActionMessage} setIsOwnerActionError={setIsOwnerActionError} />}
                </div>

                {/* Right Side Panel */}
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
                    <button onClick={handleToggle} className="w-full py-2 px-4 rounded bg-indigo-600 text-white font-semibold hover:bg-indigo-700">
                        {isAcceptingStaff ? 'Close Portal Applications' : 'Open Portal Applications'}
                    </button>
                </div>
            </div>
        </div>
    );
}
